import { Capacitor, registerPlugin } from "@capacitor/core";
import { runTransaction, serverTimestamp } from "firebase/firestore";
import { VAULTQUEST_APP_STORE_ID } from "../config/appStore";

export const APP_REVIEW_PROMPT_THRESHOLD = 3;
export const APP_REVIEW_MISSING_APP_STORE_ID = "APP_STORE_ID_MISSING";

const AppReview = registerPlugin("AppReview");

export function isNativeIosAppReviewAvailable(capacitor = Capacitor) {
  return (
    capacitor.isNativePlatform() === true &&
    capacitor.getPlatform() === "ios"
  );
}
export function getCompletedMatchCountForAppReview(profile = {}) {
  const played = Number(profile?.stats?.overall?.played);
  return Number.isFinite(played) && played >= 0 ? Math.floor(played) : 0;
}

function normalizePlayed(value) {
  const played = Number(value);
  return Number.isFinite(played) && played >= 0 ? Math.floor(played) : null;
}

export function getAppReviewPromptAction({
  profileData = {},
  completedMatches,
  threshold = APP_REVIEW_PROMPT_THRESHOLD,
} = {}) {
  const played = normalizePlayed(completedMatches);
  if (played === null) return { action: "none" };

  const appReviewState = profileData?.engagementPrompts?.appReview || {};
  if (appReviewState?.requestedAt) return { action: "none" };

  const baselinePlayed = normalizePlayed(appReviewState?.baselinePlayed);
  if (baselinePlayed === null) {
    return {
      action: "initialize",
      update: {
        "engagementPrompts.appReview.baselinePlayed": played,
        "engagementPrompts.appReview.initializedAt": serverTimestamp(),
        "engagementPrompts.appReview.updatedAt": serverTimestamp(),
      },
    };
  }

  if (played >= threshold && played > baselinePlayed) {
    return {
      action: "request",
      update: {
        "engagementPrompts.appReview.requestedAt": serverTimestamp(),
        "engagementPrompts.appReview.requestedForPlayed": played,
        "engagementPrompts.appReview.updatedAt": serverTimestamp(),
      },
    };
  }

  return { action: "none" };
}

export async function claimAppReviewPromptIfEligible({
  firestoreDb,
  userRef,
  transactionRunner = runTransaction,
} = {}) {
  if (!firestoreDb || !userRef) return false;

  return transactionRunner(firestoreDb, async (transaction) => {
    const snapshot = await transaction.get(userRef);
    if (!snapshot.exists()) return false;

    const profileData = snapshot.data() || {};
    const action = getAppReviewPromptAction({
      profileData,
      completedMatches: getCompletedMatchCountForAppReview(profileData),
    });

    if (action.action === "none") return false;
    transaction.update(userRef, action.update);
    return action.action === "request";
  });
}

export function getVaultQuestAppStoreReviewUrl(appStoreId = VAULTQUEST_APP_STORE_ID) {
  const cleanAppStoreId = String(appStoreId || "").trim();
  if (!cleanAppStoreId) return null;
  return `itms-apps://itunes.apple.com/app/id${encodeURIComponent(cleanAppStoreId)}?action=write-review`;
}

export async function requestNativeAppReviewIfAvailable({
  nativePlugin = AppReview,
  isAvailable = isNativeIosAppReviewAvailable,
} = {}) {
  if (!isAvailable()) return { requested: false, reason: "NOT_NATIVE_IOS" };
  return nativePlugin.requestReview();
}

export async function openVaultQuestAppStoreReviewPage({
  nativePlugin = AppReview,
  appStoreId = VAULTQUEST_APP_STORE_ID,
  isAvailable = isNativeIosAppReviewAvailable,
} = {}) {
  if (!isAvailable()) return { opened: false, reason: "NOT_NATIVE_IOS" };

  const url = getVaultQuestAppStoreReviewUrl(appStoreId);
  if (!url) {
    return { opened: false, reason: APP_REVIEW_MISSING_APP_STORE_ID };
  }

  return nativePlugin.openWriteReview({ url });
}
